import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { v4 as uuidv4 } from "uuid";
import { formatNumberToCurrency } from "@utils/formatNumber";
import { formatDateDMY } from "@utils/formatDate";
import {
  fetchBillAppropriation,
  fetchEapSheetItems,
  isUncertainWritebackResult,
  updateBillAppropriation,
} from "./costOriginApi";
import BillReappropriationAiPanel from "./BillReappropriationAiPanel";

const emptyNewLine = {
  unit: null,
  eapItems: [],
  loadingEap: false,
  item: null,
  percentage: "",
};

const LEVEL_LABEL = {
  cell: "célula",
  step: "etapa",
  substep: "subetapa",
  service: "serviço",
  unit: "unidade",
};
const levelLabel = (level) => LEVEL_LABEL[level] || level;
const round4 = (n) => Math.round((Number(n) || 0) * 1e4) / 1e4;
const cloneLines = (value) => value.map((line) => ({ ...line }));
const PERCENTAGE_SCALE_DIGITS = 4;
const PERCENTAGE_SCALE = 10000n;
const EXACT_TOTAL = 100n * PERCENTAGE_SCALE;
const bigIntPowerOfTen = (power) => {
  let result = 1n;
  for (let index = 0; index < power; index += 1) result *= 10n;
  return result;
};

const scaledPercentage = (value) => {
  const text = typeof value === "string" ? value.trim() : String(value ?? "");
  const numeric = Number(text);
  if (!text || !Number.isFinite(numeric) || numeric <= 0 || numeric > 100) return null;
  const match = /^\+?(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i.exec(text);
  if (!match) return null;
  const fraction = match[2] ?? "";
  const exponent = Number(match[3] ?? 0);
  if (!Number.isSafeInteger(exponent)) return null;
  const decimalPlaces = fraction.length - exponent;
  if (decimalPlaces > PERCENTAGE_SCALE_DIGITS) return null;
  const power = PERCENTAGE_SCALE_DIGITS - decimalPlaces;
  if (power < 0 || power > 20) return null;
  const scaled = BigInt(`${match[1]}${fraction}`) * bigIntPowerOfTen(power);
  return scaled > 0n && scaled <= EXACT_TOTAL ? scaled : null;
};

const itemFullPath = (line) => line.fullPath || [
  line.cellName,
  line.stepName,
  line.description
    ? `${line.costEstimationSheetId} — ${line.description}`
    : line.costEstimationSheetId,
].filter(Boolean).join(" › ");

const mapServerLines = (data, prefill) => {
  const currentLines = (data.current ?? []).map((current, index) => ({
    key: `current-${current.buildingUnitId}-${current.costEstimationSheetId}-${index}`,
    buildingId: current.buildingId,
    buildingUnitId: current.buildingUnitId,
    costEstimationSheetId: current.costEstimationSheetId,
    description: current.costEstimationSheetName,
    unitLabel: current.buildingUnitName,
    stepName: current.stepName,
    cellName: current.cellName,
    percentage: round4(current.percentage),
    isNew: false,
  }));

  if (currentLines.length === 0 && prefill?.costEstimationSheetId) {
    return [{
      key: "prefill-0",
      buildingId: data.buildingCode,
      buildingUnitId: prefill.buildingUnitId,
      costEstimationSheetId: prefill.costEstimationSheetId,
      description: prefill.description,
      percentage: 100,
      isNew: true,
    }];
  }
  return currentLines;
};

const validPlan = (plan) => Boolean(
  typeof plan?.analysisId === "string"
  && plan.analysisId
  && typeof plan?.suggestionId === "string"
  && plan.suggestionId
  && typeof plan?.currentAppropriationRevision === "string"
  && plan.currentAppropriationRevision
  && Array.isArray(plan.lines)
  && plan.lines.length >= 1
  && plan.lines.length <= 10
  && plan.lines.every((line) => line
    && typeof line.candidateRef === "string"
    && line.candidateRef
    && line.buildingId !== null
    && Number.isFinite(Number(line.buildingId))
    && Number.isInteger(line.buildingUnitId)
    && typeof line.wbsCode === "string"
    && line.wbsCode
    && Number.isInteger(line.percentageBps)
    && line.percentageBps >= 0
    && typeof line.path === "string"
    && line.path)
  && plan.lines.reduce((sum, line) => sum + line.percentageBps, 0) === 10000
);

const validAppropriationLine = (line) => {
  const percentage = scaledPercentage(line.percentage);
  return Number(line.buildingId) > 0
    && Number(line.buildingUnitId) > 0
    && String(line.costEstimationSheetId || "").trim() !== ""
    && percentage !== null;
};

const uniqueUnitId = (lines) => {
  const ids = [...new Set(lines
    .map((line) => Number(line.buildingUnitId))
    .filter(Number.isInteger))];
  return ids.length === 1 ? ids[0] : null;
};

const AppropriationEditor = ({
  open,
  onClose,
  projectId,
  billId,
  onSaved,
  prefill,
  titleInfo,
  readOnly = false,
  writebackEnabled = true,
  surfaceMode = "offcanvas",
}) => {
  const embedded = surfaceMode === "embedded";
  const scopeKey = `${open}:${writebackEnabled}:${projectId}:${billId}:${prefill?.buildingUnitId || ""}:${prefill?.costEstimationSheetId || ""}`;
  const scopeRef = useRef({ key: scopeKey, generation: 0 });
  if (scopeRef.current.key !== scopeKey) {
    scopeRef.current = {
      key: scopeKey,
      generation: scopeRef.current.generation + 1,
    };
  }
  const loadControllerRef = useRef(null);
  const eapControllerRef = useRef(null);
  const saveControllerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const saveActiveRef = useRef(false);
  const mutationLockedRef = useRef(false);
  const saveIntentRef = useRef(null);
  const preAiSnapshotRef = useRef(null);
  const lineKeyRef = useRef(0);
  const reviewHeadingRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [info, setInfo] = useState(null);
  const [lines, setLines] = useState([]);
  const [newLine, setNewLine] = useState(emptyNewLine);
  const [saving, setSaving] = useState(false);
  const [closePending, setClosePending] = useState(false);
  const [reloadRequired, setReloadRequired] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [aiLink, setAiLink] = useState(null);
  const [staleConflict, setStaleConflict] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  const scopeToken = () => ({ ...scopeRef.current });
  const isCurrentScope = (token) => scopeRef.current.key === token.key
    && scopeRef.current.generation === token.generation;

  const clearPendingClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const clearAiState = () => {
    preAiSnapshotRef.current = null;
    saveIntentRef.current = null;
    setAiLink(null);
    setStaleConflict(false);
  };

  const commitServerSnapshot = (data) => {
    setInfo({
      buildingCode: data.buildingCode,
      buildingName: data.buildingName,
      appropriationLevel: data.appropriationLevel ?? null,
      currentAppropriationRevision: data.currentAppropriationRevision ?? null,
      units: data.units ?? [],
    });
    setLines(mapServerLines(data, prefill));
    setNewLine(emptyNewLine);
    clearAiState();
    setReloadRequired(false);
  };

  const loadCurrent = async ({ preserveLinesOnError = false } = {}) => {
    const requestToken = scopeToken();
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    setLoading(true);
    setError(false);
    if (!preserveLinesOnError) setDisabled(false);
    const data = await fetchBillAppropriation(projectId, billId, {
      signal: controller.signal,
    });
    if (!isCurrentScope(requestToken) || controller.signal.aborted) return false;
    loadControllerRef.current = null;
    if (!data || data.httpStatus) {
      if (preserveLinesOnError) {
        setError(false);
        if (data?.code === "WRITEBACK_DISABLED") setDisabled(true);
      } else {
        if (data?.code === "WRITEBACK_DISABLED") setDisabled(true);
        else setError(true);
        setInfo(null);
        setLines([]);
      }
      setLoading(false);
      return data?.code === "WRITEBACK_DISABLED" ? "disabled" : false;
    }
    setDisabled(false);
    commitServerSnapshot(data);
    setLoading(false);
    return true;
  };

  useEffect(() => {
    const requestToken = scopeToken();
    loadControllerRef.current?.abort();
    eapControllerRef.current?.abort();
    saveControllerRef.current?.abort();
    clearPendingClose();
    saveActiveRef.current = false;
    mutationLockedRef.current = false;
    saveIntentRef.current = null;
    preAiSnapshotRef.current = null;
    setLoading(false);
    setError(false);
    setDisabled(false);
    setInfo(null);
    setLines([]);
    setNewLine(emptyNewLine);
    setSaving(false);
    setClosePending(false);
    setReloadRequired(false);
    setSaveResult(null);
    setAiLink(null);
    setStaleConflict(false);
    setAnnouncement("");

    if (open && writebackEnabled && projectId && billId) loadCurrent();
    return () => {
      if (isCurrentScope(requestToken)) {
        loadControllerRef.current?.abort();
        eapControllerRef.current?.abort();
        saveControllerRef.current?.abort();
        clearPendingClose();
      }
    };
  }, [scopeKey]);

  useEffect(() => {
    if (aiLink) reviewHeadingRef.current?.focus({ preventScroll: true });
  }, [aiLink?.analysisId, aiLink?.suggestionId]);

  const unitLabel = (buildingUnitId) =>
    info?.units?.find((unit) => unit.id === buildingUnitId)?.description
    || `Unidade ${buildingUnitId}`;

  const handlePercentageChange = (key, value) => {
    if (mutationLockedRef.current) return;
    setLines((previous) => previous.map((line) => (
      line.key === key ? { ...line, percentage: value } : line
    )));
  };

  const handleRemoveLine = (key) => {
    if (mutationLockedRef.current) return;
    setLines((previous) => previous.filter((line) => line.key !== key));
  };

  const handleUnitSelect = async (option) => {
    if (mutationLockedRef.current) return;
    eapControllerRef.current?.abort();
    setNewLine({ ...emptyNewLine, unit: option, loadingEap: !!option });
    if (!option) return;
    const requestToken = scopeToken();
    const controller = new AbortController();
    eapControllerRef.current = controller;
    const data = await fetchEapSheetItems(projectId, option.value, {
      signal: controller.signal,
    });
    if (
      !isCurrentScope(requestToken)
      || controller.signal.aborted
      || mutationLockedRef.current
    ) return;
    eapControllerRef.current = null;
    setNewLine((previous) => previous.unit?.value === option.value ? {
      ...previous,
      eapItems: data?.items ?? [],
      loadingEap: false,
    } : previous);
  };

  const handleAddLine = () => {
    if (
      mutationLockedRef.current
      || readOnly
      || !newLine.unit
      || !newLine.item
      || !newLine.percentage
    ) return;
    lineKeyRef.current += 1;
    setLines((previous) => [...previous, {
      key: `new-${lineKeyRef.current}`,
      buildingId: info.buildingCode,
      buildingUnitId: newLine.unit.value,
      costEstimationSheetId: newLine.item.value,
      percentage: newLine.percentage,
      unitLabel: newLine.unit.label,
      description: newLine.item.description,
      stepName: newLine.item.stepName,
      cellName: newLine.item.cellName,
      isNew: true,
    }]);
    setNewLine(emptyNewLine);
  };

  const handleApply = (plan) => {
    if (
      mutationLockedRef.current
      || readOnly
      || staleConflict
      || !writebackEnabled
      || !validPlan(plan)
    ) return;
    if (!preAiSnapshotRef.current) preAiSnapshotRef.current = cloneLines(lines);
    const mapped = plan.lines.map((line) => ({
      key: `ai-${plan.analysisId}-${plan.suggestionId}-${line.candidateRef}`,
      candidateRef: line.candidateRef,
      buildingId: line.buildingId,
      buildingUnitId: line.buildingUnitId,
      costEstimationSheetId: line.wbsCode,
      percentage: line.percentageBps / 100,
      fullPath: line.path,
      isNew: true,
    }));
    saveIntentRef.current = null;
    setLines(mapped);
    setAiLink({
      analysisId: plan.analysisId,
      suggestionId: plan.suggestionId,
      expectedAppropriationRevision: plan.currentAppropriationRevision,
    });
    setAnnouncement("Sugestão aplicada localmente. Revise o rateio antes de salvar no Sienge.");
  };

  const handleUndo = () => {
    if (mutationLockedRef.current || !preAiSnapshotRef.current) return;
    setLines(cloneLines(preAiSnapshotRef.current));
    preAiSnapshotRef.current = null;
    saveIntentRef.current = null;
    setAiLink(null);
    setAnnouncement("Rateio anterior restaurado. A sugestão da IA não foi salva.");
  };

  const total = lines.reduce(
    (sum, line) => sum + (Number(line.percentage) || 0),
    0,
  );
  const difference = round4(100 - total);
  const scaledValues = lines.map((line) => scaledPercentage(line.percentage));
  const hasInvalidPercentagePrecision = lines.some((line, index) => (
    String(line.percentage ?? "").trim() !== ""
    && Number.isFinite(Number(line.percentage))
    && Number(line.percentage) > 0
    && Number(line.percentage) <= 100
    && scaledValues[index] === null
  ));
  const scaledTotal = scaledValues.every((value) => value !== null)
    ? scaledValues.reduce((sum, value) => sum + value, 0n)
    : null;
  const totalIsExact = scaledTotal === EXACT_TOTAL;
  const interactionLocked = saving || closePending || reloadRequired || disabled;
  const linesAreValid = lines.length > 0 && lines.every(validAppropriationLine);
  const canSave = !readOnly
    && writebackEnabled
    && !staleConflict
    && linesAreValid
    && totalIsExact
    && !interactionLocked;

  const handleSave = async () => {
    if (mutationLockedRef.current || !canSave || saveActiveRef.current) return;
    const requestToken = scopeToken();
    saveActiveRef.current = true;
    mutationLockedRef.current = true;
    setSaving(true);
    setSaveResult(null);
    const appropriation = lines.map((line) => ({
      buildingId: Number(line.buildingId),
      buildingUnitId: Number(line.buildingUnitId),
      costEstimationSheetId: line.costEstimationSheetId,
      percentage: Number(line.percentage),
    }));
    const payload = {
      project_id: projectId,
      billId,
      appropriation,
      ...(aiLink ? {
        aiAnalysisId: aiLink.analysisId,
        aiSuggestionId: aiLink.suggestionId,
        expectedAppropriationRevision: aiLink.expectedAppropriationRevision,
      } : {}),
    };
    const fingerprint = JSON.stringify({ scopeKey, appropriation, aiLink });
    if (aiLink && saveIntentRef.current?.fingerprint !== fingerprint) {
      saveIntentRef.current = { fingerprint, key: uuidv4() };
    }
    const saveIntent = aiLink ? saveIntentRef.current : null;
    const controller = new AbortController();
    saveControllerRef.current?.abort();
    saveControllerRef.current = controller;
    const result = await updateBillAppropriation(payload, {
      idempotencyKey: saveIntent?.key,
      signal: controller.signal,
    });

    if (!isCurrentScope(requestToken) || controller.signal.aborted) return;
    saveControllerRef.current = null;
    let lockUntilClose = false;
    if (result?.ok) {
      saveIntentRef.current = null;
      clearAiState();
      setReloadRequired(true);
      setAnnouncement("Apropriação salva — aguardando recarga.");
      lockUntilClose = true;
      const refreshed = await loadCurrent({ preserveLinesOnError: true });
      if (!isCurrentScope(requestToken)) return;
      if (refreshed === true) {
        lockUntilClose = true;
        setClosePending(true);
        setSaveResult({ ok: true, message: result.message || "Apropriação atualizada!" });
        setAnnouncement("Apropriação salva no Sienge e dados atuais recarregados.");
        onSaved?.();
        clearPendingClose();
        closeTimerRef.current = setTimeout(() => {
          if (isCurrentScope(requestToken)) {
            mutationLockedRef.current = false;
            setClosePending(false);
            onClose?.();
          }
        }, 900);
      } else {
        setSaveResult({
          ok: false,
          reload: true,
          message: refreshed === "disabled"
            ? "Apropriação salva — aguardando recarga. A integração foi desabilitada; recarregue após reabilitar."
            : "Apropriação salva — aguardando recarga. Não foi possível recarregar os dados atuais.",
        });
      }
    } else if (result?.httpStatus === 409 && result?.code === "stale_appropriation") {
      saveIntentRef.current = null;
      setStaleConflict(true);
      setSaveResult({
        ok: false,
        stale: true,
        message: `${result.message || "A apropriação foi alterada."} Recarregue os dados atuais e revise o rateio antes de salvar novamente.`,
      });
    } else {
      if (!isUncertainWritebackResult(result)) saveIntentRef.current = null;
      setSaveResult({
        ok: false,
        message: result?.message || "Erro ao salvar a apropriação. Tente novamente.",
      });
    }
    saveActiveRef.current = false;
    if (!lockUntilClose) mutationLockedRef.current = false;
    setSaving(false);
  };

  const handleReload = async () => {
    const loaded = await loadCurrent({ preserveLinesOnError: true });
    if (loaded === true) {
      mutationLockedRef.current = false;
      setReloadRequired(false);
      setSaveResult(null);
      setAnnouncement("Dados atuais recarregados. Revise o rateio antes de salvar.");
    } else if (reloadRequired) {
      mutationLockedRef.current = true;
      setReloadRequired(true);
      if (loaded === "disabled") {
        setSaveResult({
          ok: false,
          reload: true,
          message: "Apropriação salva — aguardando recarga. A integração foi desabilitada; recarregue após reabilitar.",
        });
      }
    }
  };

  const handleClose = () => {
    scopeRef.current = {
      ...scopeRef.current,
      generation: scopeRef.current.generation + 1,
    };
    loadControllerRef.current?.abort();
    eapControllerRef.current?.abort();
    saveControllerRef.current?.abort();
    saveActiveRef.current = false;
    mutationLockedRef.current = false;
    setClosePending(false);
    setReloadRequired(false);
    clearPendingClose();
    onClose?.();
  };

  const unitOptions = (info?.units ?? []).map((unit) => ({
    value: unit.id,
    label: unit.description,
  }));
  const eapOptions = newLine.eapItems.map((item) => ({
    value: item.wbsCode,
    label: `${item.wbsCode} — ${item.description}${item.stepName ? ` — ${item.stepName}` : ""}`,
    code: item.wbsCode,
    description: item.description,
    stepName: item.stepName,
    cellName: item.cellName,
  }));

  return (
    <div className={embedded ? "appropriation-editor-embedded" : open ? "offcanvas active" : "offcanvas"}>
      <div className={embedded ? "appropriation-editor-embedded__container" : "offcanvas-container appropriation-editor"}>
        <hgroup className={embedded ? "appropriation-editor-embedded__header" : "offcanvas-header"}>
          {embedded ? (
            <button type="button" className="appropriation-editor-embedded__back" aria-label="Voltar aos títulos" onClick={handleClose}>
              ← Voltar aos títulos
            </button>
          ) : (
            <>
              <h2>{`Editar apropriação — título ${billId ?? ""}`}</h2>
              <button type="button" onClick={handleClose} aria-label="Fechar">
                <i className="icon icon-width-24 icon-times background-accent" aria-hidden={true}></i>
              </button>
            </>
          )}
        </hgroup>
        <div className={embedded ? "appropriation-editor-embedded__content" : "offcanvas-content"}>
          {titleInfo && (
            <div
              className="margin-top-8"
              style={{ padding: "10px 12px", background: "#f6f7f9", borderRadius: 6 }}
            >
              <div style={{ fontWeight: 600 }}>{titleInfo.creditor || "—"}</div>
              <div style={{ fontSize: 13, color: "#555", marginTop: 2 }}>
                {[
                  `Título ${billId ?? ""}`,
                  titleInfo.document ? `Doc ${titleInfo.document}` : null,
                  titleInfo.value != null ? formatNumberToCurrency(titleInfo.value) : null,
                  titleInfo.date ? formatDateDMY(titleInfo.date) : null,
                  titleInfo.situation || null,
                ].filter(Boolean).join(" · ")}
              </div>
            </div>
          )}

          {loading && <p className="card-label margin-top-8">Carregando…</p>}
          {disabled && !loading && (
            <p className="empty-state margin-top-16">
              Apropriação via Sienge não está habilitada para esta empresa.
            </p>
          )}
          {error && !loading && !disabled && (
            <p className="empty-state margin-top-16">
              Não foi possível carregar a apropriação deste título. Tente novamente.
            </p>
          )}

          {!loading && !error && info && (
            <>
              <p className="card-label margin-top-8">
                {info.buildingName || `Obra ${info.buildingCode}`}
              </p>
              {info.appropriationLevel && (
                <p className="card-label">
                  {`Apropriando a nível de ${levelLabel(info.appropriationLevel)}`}
                </p>
              )}

              {!readOnly && writebackEnabled && !disabled && !reloadRequired ? (
                <BillReappropriationAiPanel
                  workId={projectId}
                  billId={billId}
                  availableUnits={info.units}
                  currentUnitId={uniqueUnitId(lines)}
                  currentAppropriation={lines}
                  onApply={handleApply}
                  compactByDefault
                  readOnly={staleConflict || interactionLocked}
                  appliedSuggestionKey={aiLink
                    ? `${aiLink.analysisId}:${aiLink.suggestionId}`
                    : null}
                />
              ) : null}

              <section className="appropriation-review" aria-labelledby="appropriation-review-heading">
                <div className="appropriation-review__header">
                  <h3 id="appropriation-review-heading" ref={reviewHeadingRef} tabIndex="-1">
                    Rateio para revisão
                  </h3>
                  {reloadRequired ? (
                    <span className="appropriation-review__pending">
                      Apropriação salva — aguardando recarga
                    </span>
                  ) : aiLink ? (
                    <span className="appropriation-review__pending">
                      Sugestão da IA — ainda não salva
                    </span>
                  ) : null}
                </div>
                {aiLink ? (
                  <button
                    type="button"
                    className="appropriation-review__undo"
                    disabled={interactionLocked}
                    onClick={handleUndo}
                  >
                    Desfazer sugestão
                  </button>
                ) : null}
                <p className="appropriation-review__announcement" role="status" aria-live="polite">
                  {announcement}
                </p>

                <div className="appropriation-review__table">
                  <table>
                    <thead>
                      <tr>
                        <th>Unidade</th>
                        <th>Item EAP</th>
                        <th className="num">%</th>
                        <th aria-label="Ações"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => {
                        const path = itemFullPath(line);
                        const itemLabel = line.fullPath
                          || (line.description
                            ? `${line.costEstimationSheetId} — ${line.description}`
                            : line.costEstimationSheetId);
                        return (
                          <tr key={line.key}>
                            <td>{line.unitLabel || unitLabel(line.buildingUnitId)}</td>
                            <td title={path}>
                              <div title={path}>{itemLabel}</div>
                              {!line.fullPath && line.stepName ? <small>{line.stepName}</small> : null}
                            </td>
                            <td className="num">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.0001"
                                value={line.percentage}
                                aria-label={`Percentual de ${path}`}
                                disabled={readOnly || interactionLocked}
                                onChange={(event) => handlePercentageChange(line.key, event.target.value)}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="button button-error small"
                                disabled={readOnly || interactionLocked}
                                aria-label={`Remover ${path}`}
                                onClick={() => handleRemoveLine(line.key)}
                              >
                                Remover
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {lines.length === 0 && (
                        <tr>
                          <td colSpan={4} className="empty-state">
                            Nenhuma linha de apropriação. Adicione uma abaixo.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <hr />
              <h3>Adicionar linha</h3>
              <div className="appropriation-add-line margin-top-8">
                <div>
                  <label>Unidade construtiva:</label>
                  <Select
                    value={newLine.unit}
                    onChange={handleUnitSelect}
                    options={unitOptions}
                    placeholder="Escolher unidade"
                    isDisabled={readOnly || interactionLocked}
                    isClearable
                    classNamePrefix="appropriationUnit"
                  />
                </div>
                <div>
                  <label>Item da EAP:</label>
                  <Select
                    value={newLine.item}
                    onChange={(option) => {
                      if (!mutationLockedRef.current) {
                        setNewLine((previous) => ({ ...previous, item: option }));
                      }
                    }}
                    options={eapOptions}
                    placeholder={newLine.loadingEap ? "Carregando…" : "Escolher item"}
                    isDisabled={readOnly || interactionLocked || !newLine.unit || newLine.loadingEap}
                    isClearable
                    classNamePrefix="appropriationEap"
                    formatOptionLabel={(option) => (
                      <div title={itemFullPath({ ...option, costEstimationSheetId: option.code })}>
                        <div>{`${option.code} — ${option.description}`}</div>
                        {option.stepName ? <small>{option.stepName}</small> : null}
                      </div>
                    )}
                  />
                </div>
                <div>
                  <label htmlFor="appropriation-new-percentage">%:</label>
                  <input
                    id="appropriation-new-percentage"
                    aria-label="Percentual da nova linha"
                    type="number"
                    min="0"
                    max="100"
                    step="0.0001"
                    value={newLine.percentage}
                    disabled={readOnly || interactionLocked}
                    onChange={(event) => {
                      if (!mutationLockedRef.current) {
                        setNewLine((previous) => ({
                          ...previous,
                          percentage: event.target.value,
                        }));
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  className={`button button-${newLine.unit && newLine.item && newLine.percentage ? "accent" : "grey"}`}
                  disabled={readOnly || interactionLocked || !newLine.unit || !newLine.item || !newLine.percentage}
                  onClick={handleAddLine}
                >
                  Adicionar
                </button>
              </div>

              <div className="appropriation-editor__footer">
                <div className="appropriation-editor__totals" role="status" aria-live="polite">
                  <span>{`Total: ${total.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4,
                  })}%`}</span>
                  <span>{`Diferença: ${Math.abs(difference).toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4,
                  })}%`}</span>
                  <strong className={canSave ? "appropriation-editor__valid" : "appropriation-editor__invalid"}>
                    {hasInvalidPercentagePrecision
                      ? "Use no máximo 4 casas decimais"
                      : totalIsExact ? "Rateio válido" : "O rateio precisa somar 100%"}
                  </strong>
                </div>
                <div className="appropriation-save-row">
                  <div>
                    {saveResult ? (
                      <div role={saveResult.ok ? "status" : "alert"}>
                        <p className={saveResult.ok ? "bold" : "appropriation-save-error"}>
                          {saveResult.message}
                        </p>
                        {saveResult.stale || saveResult.reload ? (
                          <button type="button" onClick={handleReload}>
                            Recarregar dados atuais
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={`button button-${canSave ? "accent" : "grey"}`}
                    disabled={!canSave}
                    onClick={handleSave}
                  >
                    {saving ? "Salvando…" : "Salvar no Sienge"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {!embedded && open && <div onClick={handleClose} className="offcanvas-background" />}
    </div>
  );
};

export default AppropriationEditor;
